import React, { useState, useEffect, useRef } from 'react';
import socket, { startSupportChat, joinSupportTicket, fetchSupportHistory, sendSupportMessage, closeSupportTicket } from './services/socket';
import { getSystems, createTicket } from '../../models/helpdeskModel';
import { useAuth } from '../../context/AuthContext';

// Style for fixing dropdown z-index issues
const dropdownStyles = `
  .chat-widget-select {
    position: relative;
  }

  .chat-widget-select select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  .chat-widget-select select:focus {
    outline: none;
    border-color: #63b3ed;
    box-shadow: 0 0 0 1px #63b3ed;
  }

  .chat-widget-select select option {
    position: relative;
  }
`;

const ChatWidget = ({ isOpen = true, onClose }) => {
  console.log('[ChatWidget] render start, isOpen prop =', isOpen);
  const { user, originalUser } = useAuth();
  
  // State management
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: "Hello! How can we help you today?", sender: "agent", timestamp: new Date() }
  ]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [category, setCategory] = useState("0");
  const [subject, setSubject] = useState("");
  const [details, setDetails] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [isChatClosed, setIsChatClosed] = useState(false);
  const [isConnected, setIsConnected] = useState(socket.connected);
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const onConnect = () => setIsConnected(true);
    const onDisconnect = () => setIsConnected(false);

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, []);
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Take a screenshot function
  const takeScreenshot = () => {
    // This is a placeholder for screenshot functionality
    // In a real implementation, you would use a library like html2canvas
    alert('Screenshot feature would capture the current screen');
    
    // In a real implementation:
    // html2canvas(document.body).then(canvas => {
    //   const imgData = canvas.toDataURL('image/png');
    //   // Send this data to your server or add to chat
    //   addScreenshotMessage(imgData);
    // });
  };
  
  // Add screenshot to chat
  const addScreenshotMessage = (imageData) => {
    const screenshotMsg = {
      id: messages.length + 1,
      text: "Screenshot taken",
      sender: "user",
      timestamp: new Date(),
      isImage: true,
      imageUrl: imageData
    };
    
    setMessages([...messages, screenshotMsg]);
  };

  // Load chat messages from the server - used as initial load and fallback
  const loadMessages = async () => {
    try {
      const channelId = sessionStorage.getItem('channelId');
      
      if (!channelId) {
        console.error('No channel ID available for loading messages');
        return;
      }
      
      console.log(`Loading initial messages for channel: ${channelId}`);
      
      const userId = user?.user_id || localStorage.getItem('user_id') || sessionStorage.getItem('temp_user_id') || '1';
      const userName = user?.user_full_name || localStorage.getItem('user_name') || sessionStorage.getItem('temp_user_name') || 'Guest User';
      const userEmail = user?.user_email || localStorage.getItem('user_email') || '';
      
      // Helper to check if user is IT
      const isITUser = () => {
          const deptId = user?.department ? parseInt(user.department) : parseInt(localStorage.getItem('department_id') || '0');
          const role = user?.role_name || localStorage.getItem('user_role') || '';
          return role === 'IT' || deptId === 27 || deptId === 21 || deptId === 24;
      };

      let data = null;
      try {
        const response = await fetchSupportHistory(channelId, { markRead: false });
        data = response;
      } catch (socketError) {
        console.warn('Socket history fetch failed, falling back to REST API', socketError);
        data = await fallbackLoadMessages(channelId);
      }
      
      if (data && data.messages && Array.isArray(data.messages)) {
        console.log(`Received ${data.messages.length} messages`);
        
        const currentUserId = localStorage.getItem('user_id');
        const isIT = () => {
            const deptId = parseInt(localStorage.getItem('department_id') || '0');
            const role = localStorage.getItem('user_role') || '';
            return role === 'IT' || deptId === 27 || deptId === 21 || deptId === 24;
        };

        // Transform messages to match expected format
        const formattedMessages = data.messages.map((msg, index) => {
          // Logic from HelpdeskApiChat.jsx
          const messageFromCurrentUser = msg.userId == currentUserId;
          const userIsIT = isIT();
          
          return {
            id: msg.id || msg.comment_id || index + 1,
            text: msg.content || msg.message || '(Empty message)',
            sender: messageFromCurrentUser ? 'user' : 'agent', // Simplified for UI
            isUser: messageFromCurrentUser,
            isCurrentUserMessage: messageFromCurrentUser,
            incoming: msg.incoming ?? (messageFromCurrentUser ? 0 : 1), // Simplified direction
            timestamp: msg.timestamp ? new Date(msg.timestamp * 1000) : new Date(),
            closed: msg.closed === 1,
            // Add additional metadata if available
            attachment: msg.attachments || null,
            attachmentUrl: msg.attachment_url || null,
            userText: msg.userName || msg.name || 'System',
            isInternal: msg.is_internal === 1 || msg.is_internal === true
          };
        });
        
        console.log('Formatted messages:', formattedMessages);
        setMessages(formattedMessages);
        
        // If messages loaded successfully, scroll to bottom
        setTimeout(scrollToBottom, 100);
        
        // Check if the chat is closed in the last message
        const lastMessage = data.messages[data.messages.length - 1];
        if (lastMessage && lastMessage.closed === 1) {
          handleChatClosed();
        }
      } else {
        console.warn('Invalid or empty messages data structure:', data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      
      // Only add the error message if we're in the chat window
      if (showChatWindow) {
        const errorExists = messages.some(m => m.isError && m.text.includes('trouble loading'));
        
        if (!errorExists) {
          setMessages(prev => [
            ...prev,
            {
              id: prev.length + 1,
              text: "We're having trouble loading messages. Please refresh the page.",
              sender: "system",
              timestamp: new Date(),
              isError: true
            }
          ]);
        }
      }
    }
  };
  
  // Fallback function to load messages via REST API if socket fails
  const fallbackLoadMessages = async (channelId) => {
    console.log(`Falling back to REST API for messages, channel: ${channelId}`);
    
    // Import API_BASE_URL from config
    const { API_BASE_URL } = await import('../../config');
    // Use the ticket comments endpoint as in HelpdeskApiChat.jsx
    const apiUrl = `${API_BASE_URL}/api/helpdesk/tickets/${channelId}/comments`;
    console.log(`Fetching helpdesk messages from: ${apiUrl}`);
    
    const response = await fetch(apiUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error loading messages response:', errorText);
      throw new Error(`Failed to load messages: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Normalize the response structure to match what loadMessages expects
    // HelpdeskApiChat.jsx handles data.data.comments or data.data
    const comments = data.data?.comments || data.data || [];
    
    return { messages: comments };
  };
  
  // Set up socket event listeners
  const setupSocketListeners = () => {
    console.log('Setting up socket event listeners for chat');
    cleanupSocketListeners();

    socket.on('support:ticket:history:response', (payload) => {
      const channelMessages = payload?.messages;
      if (Array.isArray(channelMessages)) {
        const formattedMessages = channelMessages.map((msg, index) => ({
          id: msg.comment_id || msg.id || index + 1,
          text: msg.content || msg.message || '(Empty message)',
          sender: msg.incoming === 0 ? 'agent' : 'user',
          timestamp: msg.created_at ? new Date(msg.created_at) : new Date(),
          closed: msg.closed === 1,
          attachment: msg.attachments || msg.attachment || null,
          attachmentUrl: msg.attachment_url || null
        }));
        setMessages(formattedMessages);
        setTimeout(scrollToBottom, 100);
      }
    });

    socket.on('support:message', (data) => {
      console.log('🔔 [Web] Received support message:', data);

      const comment = data?.comment ? data.comment : data;
      if (comment) {
        const currentUserId = user?.user_id || localStorage.getItem('user_id');
        const messageFromCurrentUser = String(comment.user_id) === String(currentUserId);

        const formattedMessage = {
          id: comment.comment_id || comment.id || Date.now(),
          text: comment.content || comment.message || '(Empty message)',
          sender: messageFromCurrentUser ? 'user' : 'agent',
          timestamp: comment.created_at ? new Date(comment.created_at) : new Date(),
          isUser: messageFromCurrentUser,
          isCurrentUserMessage: messageFromCurrentUser,
          incoming: (comment.incoming !== undefined && comment.incoming !== null)
            ? comment.incoming
            : (messageFromCurrentUser ? 1 : 0),
          attachment: comment.attachments || null,
          userText: comment.user_name || 'System',
          isInternal: comment.is_internal === 1 || comment.is_internal === true
        };

        setMessages((prevMessages) => {
          const messageExists = prevMessages.some((m) => m.id === formattedMessage.id);

          if (!messageExists) {
            const newMessages = [...prevMessages, formattedMessage];
            setTimeout(scrollToBottom, 100);
            return newMessages;
          }
          return prevMessages;
        });
      }
    });

    socket.on('support:ticket:closed', (data) => {
      console.log('Chat was closed by agent:', data);
      handleChatClosed();
    });

    socket.on('support:error', (error) => {
      console.error('Socket error in help chat:', error);

      setMessages((prev) => {
        const errorExists = prev.some((m) => m.isError && m.text.includes(error.message || 'error'));

        if (!errorExists) {
          return [
            ...prev,
            {
              id: prev.length + 1,
              text: error.message || 'An error occurred with the chat connection',
              sender: 'system',
              timestamp: new Date(),
              isError: true
            }
          ];
        }

        return prev;
      });
    });
  };
  
  // Helper function to handle chat closed state
  const handleChatClosed = () => {
    if (!isChatClosed) {
      setIsChatClosed(true);
      
      setMessages(prev => {
        // Check if we already have a closed message
        const hasClosedMessage = prev.some(msg => 
          msg.sender === 'system' && msg.text.includes('This chat has been closed')
        );
        
        if (!hasClosedMessage) {
          return [
            ...prev, 
            {
              id: prev.length + 1,
              text: "This chat has been closed. Please start a new chat if you need further assistance.",
              sender: "system",
              timestamp: new Date(),
              isChatClosed: true
            }
          ];
        }
        
        return prev;
      });
    }
  };
  
  // Clean up socket event listeners
  const cleanupSocketListeners = () => {
    socket.off('support:ticket:history:response');
    socket.off('support:message');
    socket.off('support:ticket:closed');
    socket.off('support:error');
  };

  useEffect(() => {
    const shouldOpen = true; // forced open for testing
    console.log('[ChatWidget] useEffect fired, user =', user, 'shouldOpen =', shouldOpen);

    if (shouldOpen) {
      console.log('[ChatWidget] opening logic running');
      // Fetch categories when chat opens
      fetchCategories();
      
      // Check for existing chat session
      const existingChannelId = sessionStorage.getItem('channelId');
      console.log('ChatWidget opened - existing channelId:', existingChannelId);
      
      if (existingChannelId) {
        console.log('[ChatWidget] found existing channelId in sessionStorage:', existingChannelId);
        // Verify the channel is still valid by checking if it's not too old
        const channelTime = sessionStorage.getItem('channelTime');
        const currentTime = Math.floor(Date.now() / 1000);
        const maxChatAge = 24 * 60 * 60; // 24 hours in seconds
        
        if (channelTime && (currentTime - parseInt(channelTime)) > maxChatAge) {
          // Channel is too old, clear it and start fresh
          console.log('[ChatWidget] existing channel is too old, clearing session');
          sessionStorage.removeItem('channelId');
          sessionStorage.removeItem('channelTime');
          setShowChatWindow(false);
        } else {
          // Reset closed state when loading an existing chat
          console.log('[ChatWidget] loading existing chat session');
          setIsChatClosed(false);
          
          // Load initial messages once
          loadMessages();
          setShowChatWindow(true);
          
          // Join the channel with socket
          const userId = user?.user_id || localStorage.getItem('user_id') || sessionStorage.getItem('temp_user_id') || '1';
          const userName = user?.user_full_name || localStorage.getItem('user_name') || sessionStorage.getItem('temp_user_name') || 'Guest User';
          const userEmail = user?.user_email || localStorage.getItem('user_email') || '';
          
          // Join the ticket room for real-time updates
          joinSupportTicket(existingChannelId, {
            user_id: userId,
            user_name: userName,
            user_email: userEmail
          }).catch((err) => {
            console.error('Failed to rejoin support ticket:', err);
          });
          
          // Set up socket event listeners for real-time updates
          setupSocketListeners();
        }
      } else {
        // No existing session, ensure we show the form
        console.log('[ChatWidget] no existing chat session, showing new chat form');
        setShowChatWindow(false);
        setIsChatClosed(false);
      }
    }
    
    // Cleanup function to remove socket listeners when component unmounts
    return () => {
      console.log('[ChatWidget] cleanup effect running, removing socket listeners');
      cleanupSocketListeners();
      
      // Socket disconnect will handle leaving the channel automatically
    };
  }, [user]);
  
  // Reset category when categories are loaded
  useEffect(() => {
    if (categories && categories.length > 0) {
      // Check if we need to reset the category
      if (category === "0") {
        // Uncomment the line below if you want to automatically select the first category
        // setCategory(categories[0].id.toString());
      }
    }
  }, [categories]);
  
  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoading(true);
      
      const systemsResponse = await getSystems();
      
      if (systemsResponse.success || systemsResponse.status === 1) {
        // Extract the systems from the nested data structure
        const systemsData = systemsResponse.data?.data?.rows || systemsResponse.data || [];
        
        // Filter out systems with empty names
        const validSystems = systemsData.filter(system => {
          const hasName = system.asset_name && system.asset_name.trim() !== ''; 
          return hasName;
        });

        const formattedCategories = validSystems.map(system => ({
            id: system.asset_id.toString(),
            name: system.asset_name
        }));
        
        setCategories(formattedCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Start a new chat
  const startChat = async () => {
    // Validate form fields with more descriptive messages
    if (category === "0") {
      alert("Please select a category from the dropdown");
      return;
    }
    
    if (!subject.trim()) {
      alert("Please enter a subject for your chat");
      return;
    }
    
    if (!details.trim()) {
      alert("Please provide details about your issue");
      return;
    }
    
    setLoading(true);
    
    try {
      // Get user information from AuthContext with better fallbacks
      const userId = user?.user_id || localStorage.getItem('user_id') || sessionStorage.getItem('temp_user_id') || '1';
      const userName = user?.user_full_name || localStorage.getItem('user_name') || sessionStorage.getItem('temp_user_name') || 'Guest User';
      const userEmail = user?.user_email || localStorage.getItem('user_email') || '';
      
      console.log('Starting chat with user:', userId, userName);
      
      // Prepare chat data for socket
      const chatData = {
        userId: userId,
        name: userName,
        email: userEmail,
        subject: subject,
        details: details,
        description: details,
        system: category,
        category: category,
        department: 1 // Default department
      };
      
      // Start chat via Socket
      const response = await startSupportChat(chatData);
      
      if (response && (response.status === 1 || response.success)) {
        const channelId = response.channel_id || response.ticketId;
        
        if (!channelId) {
          throw new Error('No channel ID received from server');
        }
        
        console.log('Chat started successfully via socket:', channelId);
        
        // Store channel ID in session storage for persistence
        sessionStorage.setItem('channelId', channelId.toString());
        sessionStorage.setItem('channelTime', Math.floor(Date.now() / 1000).toString());
        
        // Set up socket event listeners
        setupSocketListeners();
        
        // Get category name for the message
        const selectedCategory = categories.find(c => c.id.toString() === category.toString());
        const categoryName = selectedCategory ? selectedCategory.name : "Selected category";
        
        // Add system messages
        setMessages([
          { id: 1, text: "Welcome to IT Helpdesk. How can we assist you today?", sender: "agent", timestamp: new Date() },
          { 
            id: 2, 
            text: `New chat started: ${subject} (${categoryName})`, 
            sender: "system", 
            timestamp: new Date() 
          },
          {
            id: 3,
            text: details,
            sender: "user",
            timestamp: new Date()
          }
        ]);
        
        setShowChatWindow(true);
      } else {
        throw new Error(response.message || 'Failed to start chat');
      }
      
    } catch (error) {
      console.error("Error starting chat:", error);
      alert("Failed to start chat. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const sendMessage = async () => {
    if (!newMessage.trim()) {
      console.log('Attempted to send empty message, ignoring');
      return;
    }
    
    // Store message text before clearing input
    const messageText = newMessage;
    console.log('Preparing to send message:', messageText);
    
    // Add user message immediately for better UX
    const userMessage = {
      id: messages.length + 1,
      text: messageText,
      sender: "user",
      timestamp: new Date()
    };
    
    setMessages([...messages, userMessage]);
    setNewMessage(""); // Clear input immediately for better UX
    
    try {
      // Get user information from AuthContext with better fallbacks
      const userId = user?.user_id || localStorage.getItem('user_id') || sessionStorage.getItem('temp_user_id') || '1';
      const userName = user?.user_full_name || localStorage.getItem('user_name') || sessionStorage.getItem('temp_user_name') || 'Guest User';
      const userEmail = user?.user_email || localStorage.getItem('user_email') || sessionStorage.getItem('temp_user_email') || '';
      
      console.log('Sending message as user:', userId, userName, userEmail);
      
      // Get channel ID from session storage
      const channelId = sessionStorage.getItem('channelId');
      
      if (!channelId) {
        console.error('No channel ID found in session storage');
        throw new Error('No active chat session found. Please restart the chat.');
      }
      
      console.log('Using channel ID:', channelId);
      
      // Prepare message data
      const messageData = {
        channel_id: channelId,
        ticket_id: channelId, // Add ticket_id for API-based chat
        content: messageText,
        user: userId,
        name: userName,
        userName: userName,
        userId: userId,
        email: userEmail
      };
      
      
      // Send message via socket.io using helpdesk function
      await sendSupportMessage(messageData);
      
      // Scroll to bottom after sending
      setTimeout(scrollToBottom, 100);
      
    } catch (error) {
      console.error("Error sending message:", error);
      
      // Add a more descriptive error message
      let errorMessage = "Failed to send message. Please try again.";
      
      if (error.message.includes('No active chat session')) {
        errorMessage = "Your chat session has expired. Please start a new chat.";
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error.message.includes('500')) {
        errorMessage = "Server error. Our team has been notified.";
      }
      
      // Add error message to chat
      setMessages(prev => [
        ...prev,
        {
          id: prev.length + 1,
          text: errorMessage,
          sender: "system",
          timestamp: new Date(),
          isError: true
        }
      ]);
    }
  };

  // End the chat
  const endChat = async () => {
    if (window.confirm("Are you sure you want to end this chat?")) {
      setLoading(true);

      try {
        const channelId = sessionStorage.getItem('channelId');

        if (!channelId) {
          throw new Error('No active chat session found');
        }

        await closeSupportTicket(channelId, 'Chat ended by user');

        cleanupSocketListeners();
        sessionStorage.removeItem('channelId');
        sessionStorage.removeItem('channelTime');

        setShowChatWindow(false);
        setMessages([
          { id: 1, text: "Hello! How can we help you today?", sender: "agent", timestamp: new Date() }
        ]);
        setSubject("");
        setDetails("");
        setCategory("0");
        setIsChatClosed(false);

        setTimeout(() => {
          alert("Chat ended successfully");
        }, 300);
      } catch (error) {
        console.error("Error ending chat:", error);
        alert(error.message || "Failed to end chat. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Log chat as ticket
  const logAsTicket = async () => {
    if (window.confirm("Log this chat as a support ticket?")) {
      setLoading(true);
      
      try {
        // Get channel ID from session storage
        const channelId = sessionStorage.getItem('channelId');
        
        if (!channelId) {
          throw new Error('No active chat session found');
        }
        
        // Get user information from AuthContext
        const userId = user?.user_id || localStorage.getItem('user_id') || '1';
        
        // Prepare ticket data
        const ticketData = {
          channel_id: channelId,
          user_id: userId
        };
        
        const ticketResult = await new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            reject(new Error('Convert to ticket request timed out'));
          }, 10000);

          socket.emit('support:ticket:convert', ticketData, (response) => {
            clearTimeout(timeoutId);
            if (response && response.success) {
              resolve(response);
            } else {
              reject(new Error(response?.error || 'Error creating ticket'));
            }
          });
        });

        const data = ticketResult;
        
        // Leave the channel
        // Clean up socket listeners
        cleanupSocketListeners();
        
        // Show confirmation with real ticket number
        alert(`Chat logged as support ticket #${data.ticket_id || 'Unknown'}`);
        
        // Remove channel ID from session storage
        sessionStorage.removeItem('channelId');
        
        // Reset chat state
        setShowChatWindow(false);
        setMessages([
          { id: 1, text: "Hello! How can we help you today?", sender: "agent", timestamp: new Date() }
        ]);
        setSubject("");
        setDetails("");
        setCategory("0");
        setIsChatClosed(false);
        
      } catch (error) {
        console.error("Error logging chat as ticket:", error);
        alert("Failed to create ticket. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle Enter key for sending messages
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Only show chat widget for user with ID 19 when NOT masquerading
  // Hide completely when masquerading
  console.log('ChatWidget render check:', { 
    isOpen, 
    originalUser: originalUser?.user_id, 
    user: user?.user_id,
    shouldHide: !isOpen || originalUser || !user || (String(user.user_id) !== '19')
  });
  
  if (!isOpen || originalUser || !user || (String(user.user_id) !== '19')) return null;

  return (
    <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col z-[60]">
      {/* Add styles to fix dropdown z-index */}
      <style dangerouslySetInnerHTML={{ __html: dropdownStyles }} />
      {/* Chat Header */}
      <div className="bg-blue-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-blue-700 flex items-center justify-center">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div>
            <span className="font-medium">IT Helpdesk</span>
            <span 
              className={`ml-2 inline-block w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`} 
              title={isConnected ? 'Connected' : 'Disconnected'}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading && (
            <div className="animate-spin">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          )}
          <button 
            onClick={takeScreenshot}
            className="text-white hover:text-gray-200" 
            title="Take Screenshot"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
          <button 
            onClick={() => {
              // Clear existing session and start fresh
              sessionStorage.removeItem('channelId');
              sessionStorage.removeItem('channelTime');
              setShowChatWindow(false);
              setIsChatClosed(false);
              setMessages([{ id: 1, text: "Hello! How can we help you today?", sender: "agent", timestamp: new Date() }]);
              setSubject("");
              setDetails("");
              setCategory("0");
              cleanupSocketListeners();
            }}
            className="text-white hover:text-gray-200" 
            title="Start New Chat"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Chat Window - For ongoing chats */}
      {showChatWindow ? (
        <>
          {/* Messages Area */}
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            {messages.map((message) => (
              <div key={message.id} className={`mb-4 ${message.sender === 'user' ? 'text-right' : ''}`}>
                {message.sender === 'system' ? (
                  <div className={`
                    ${message.isError ? 'bg-red-100 border border-red-300' : 
                      message.isChatClosed ? 'bg-yellow-100 border border-yellow-300' : 
                      'bg-gray-200'} 
                    rounded-lg p-3 inline-block max-w-[80%] mx-auto text-center
                  `}>
                    {message.isError && (
                      <svg className="w-5 h-5 text-red-600 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {message.isChatClosed && (
                      <svg className="w-5 h-5 text-yellow-600 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <p className={`text-sm ${
                      message.isError ? 'text-red-800' : 
                      message.isChatClosed ? 'text-yellow-800' : 
                      'text-gray-800'
                    }`}>{message.text}</p>
                  </div>
                ) : message.isImage ? (
                  <div className={`${message.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'} rounded-lg p-3 inline-block max-w-[80%]`}>
                    <p className="text-sm mb-2">{message.text}</p>
                    <img 
                      src={message.imageUrl} 
                      alt="Screenshot" 
                      className="max-w-full rounded border border-gray-200"
                    />
                  </div>
                ) : (
                  <div className={`${message.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'} rounded-lg p-3 inline-block max-w-[80%]`}>
                    <p className="text-sm">{message.text}</p>
                    {message.attachment && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <a 
                          href={message.attachmentUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs flex items-center text-blue-500 hover:underline"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          {message.attachment}
                        </a>
                      </div>
                    )}
                  </div>
                )}
                <div className={`text-xs text-gray-500 mt-1 ${message.sender === 'user' ? 'text-right' : ''}`}>
                  {message.sender === 'user' ? 'You' : 'Support Agent'} • {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Chat Actions */}
          <div className="p-3 border-t border-gray-200">
            {isChatClosed ? (
              <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 mb-3 text-center">
                <p className="text-yellow-800 text-sm font-medium mb-2">This chat has been closed</p>
                <div className="flex gap-2">
                  <button 
                    onClick={() => {
                      sessionStorage.removeItem('channelId');
                      setShowChatWindow(false);
                      setIsChatClosed(false);
                    }}
                    className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm hover:bg-blue-700"
                  >
                    Start New Chat
                  </button>
                  <button 
                    onClick={logAsTicket}
                    className="flex-1 bg-green-600 text-white rounded-lg py-2 text-sm hover:bg-green-700"
                  >
                    Log as Ticket
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-3">
                  <button 
                    onClick={endChat} 
                    className="flex-1 bg-red-600 text-white rounded-lg py-2 text-sm hover:bg-red-700"
                  >
                    End Chat
                  </button>
                  <button 
                    onClick={logAsTicket}
                    className="flex-1 bg-blue-600 text-white rounded-lg py-2 text-sm hover:bg-blue-700"
                  >
                    Log as Ticket
                  </button>
                </div>
                <div className="flex gap-2">
                  <textarea 
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" 
                    placeholder="Type here and press enter to send"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows="2"
                    disabled={loading}
                  />
                  <button 
                    className={`${loading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg p-2 transition-colors`}
                    onClick={sendMessage}
                    disabled={loading}
                  >
                    {loading ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        // Chat Intro Form - For starting a new chat
        <div className="flex-1 p-4 overflow-y-auto">
          <div className="space-y-4">
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <div className="chat-widget-dropdown">
                <select
                  className="chat-widget-select"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="0">Select Category</option>
                  {categories && categories.length > 0 ? (
                    categories.map((cat, index) => {
                      // Handle any possible data structure in the categories array
                      // This ensures we always have valid ID and name values
                      const id = cat.id || '';
                      const name = typeof cat.name === 'object' ? 
                        (cat.name?.asset_name || cat.name?.name || `Category ${id}`) : 
                        cat.name?.toString() || `Category ${id}`;
                      
                      return (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      );
                    })
                  ) : (
                    <option value="0" disabled>No categories available</option>
                  )}
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe your issue"
                rows="4"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
              />
            </div>
            
            <button
              onClick={startChat}
              disabled={loading}
              className="w-full bg-green-600 text-white rounded-lg py-2 text-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : "Start Chat"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;
